/**
 * Secure Logging Utility
 * Prevents sensitive data from being exposed in console logs
 */

// Patterns to identify sensitive data
const SENSITIVE_PATTERNS = [
  /password/i,
  /secret/i,
  /token/i,
  /key/i,
  /auth/i,
  /session/i,
  /jwt/i,
  /bearer/i,
  /api[_-]?key/i,
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /supabase/i,
  /connection[_-]?string/i,
  /credential/i,
  /private/i,
  /sign/i
];

// Keys that should never be logged
const BLOCKED_KEYS = [
  'password',
  'secret',
  'token',
  'key',
  'auth',
  'jwt',
  'bearer',
  'access_token',
  'refresh_token',
  'session',
  'supabase_key',
  'api_key',
  'private_key',
  'connection_string',
  'credentials'
];

/**
 * Sanitize sensitive data from objects before logging
 */
function sanitizeData(data, depth = 0) {
  if (depth > 3) return '[Max Depth Reached]';
  
  if (data === null || data === undefined) return data;
  
  if (typeof data === 'string') {
    return sanitizeString(data);
  }
  
  if (typeof data === 'object') {
    if (Array.isArray(data)) {
      return data.map(item => sanitizeData(item, depth + 1));
    }
    
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      
      // Block sensitive keys entirely
      if (BLOCKED_KEYS.some(blocked => lowerKey.includes(blocked))) {
        sanitized[key] = '[REDACTED]';
        continue;
      }
      
      // Check for sensitive patterns in key names
      if (SENSITIVE_PATTERNS.some(pattern => pattern.test(key))) {
        sanitized[key] = obfuscateValue(value);
        continue;
      }
      
      sanitized[key] = sanitizeData(value, depth + 1);
    }
    return sanitized;
  }
  
  return data;
}

/**
 * Sanitize sensitive data from strings
 */
function sanitizeString(str) {
  if (typeof str !== 'string') return str;
  
  // Remove JWT tokens
  str = str.replace(/eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*/g, '[JWT_TOKEN_REDACTED]');
  
  // Remove API keys (common patterns)
  str = str.replace(/sk-[a-zA-Z0-9]{32,}/g, '[API_KEY_REDACTED]');
  str = str.replace(/pk_[a-zA-Z0-9]{24,}/g, '[PUBLIC_KEY_REDACTED]');
  
  // Remove connection strings
  str = str.replace(/[a-zA-Z0-9+/]{32,}={0,2}/g, '[BASE64_REDACTED]');
  
  return str;
}

/**
 * Obfuscate sensitive values while keeping some info for debugging
 */
function obfuscateValue(value) {
  if (typeof value === 'string') {
    if (value.length <= 4) return '[REDACTED]';
    if (value.length <= 8) return `${value.substring(0, 2)}***`;
    return `${value.substring(0, 4)}***${value.substring(value.length - 4)}`;
  }
  return '[REDACTED]';
}

/**
 * Production-safe logger
 */
class SecureLogger {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.debugMode = process.env.REACT_APP_DEBUG_MODE === 'true';
  }

  log(message, data = null, level = 'info') {
    // Suppress debug messages in all environments to keep console clean
    if (level === 'debug') return;
    
    if (this.isProduction && !this.debugMode) {
      // In production, only log errors and warnings
      if (level !== 'error' && level !== 'warn') return;
    }

    const sanitizedData = data ? sanitizeData(data) : null;
    
    switch (level) {
      case 'error':
        console.error(`[${this.getTimestamp()}] ${message}`, sanitizedData);
        break;
      case 'warn':
        console.warn(`[${this.getTimestamp()}] ${message}`, sanitizedData);
        break;
      case 'info':
        console.info(`[${this.getTimestamp()}] ${message}`, sanitizedData);
        break;
      case 'debug':
        if (!this.isProduction || this.debugMode) {
          console.log(`[${this.getTimestamp()}] DEBUG: ${message}`, sanitizedData);
        }
        break;
      default:
        console.log(`[${this.getTimestamp()}] ${message}`, sanitizedData);
    }
  }

  error(message, data = null) {
    this.log(message, data, 'error');
  }

  warn(message, data = null) {
    this.log(message, data, 'warn');
  }

  info(message, data = null) {
    this.log(message, data, 'info');
  }

  debug(message, data = null) {
    this.log(message, data, 'debug');
  }

  getTimestamp() {
    return new Date().toISOString();
  }

  // Method to completely disable logging in production
  silence() {
    if (this.isProduction) {
      console.log = () => {};
      console.info = () => {};
      console.warn = () => {};
      console.debug = () => {};
      // Keep console.error for critical issues
    }
  }

  // Method to clean up console for better UX
  cleanConsole() {
    // Suppress common development noise
    const originalLog = console.log;
    const originalInfo = console.info;
    const originalWarn = console.warn;
    
    console.log = (...args) => {
      const message = args.join(' ');
      // Filter out noisy development messages
      if (message.includes('[API Request]') || 
          message.includes('[Auth]') || 
          message.includes('Setting auth as ready') ||
          message.includes('Session status') ||
          message.includes('Token attached') ||
          message.includes('CircuitBreaker') ||
          message.includes('React DevTools') ||
          message.includes('Future Flag Warning')) {
        return;
      }
      originalLog.apply(console, args);
    };

    console.info = (...args) => {
      const message = args.join(' ');
      if (message.includes('React DevTools')) {
        return;
      }
      originalInfo.apply(console, args);
    };

    console.warn = (...args) => {
      const message = args.join(' ');
      if (message.includes('Future Flag Warning') || 
          message.includes('React Router') ||
          message.includes('mozInputSource')) {
        return;
      }
      originalWarn.apply(console, args);
    };
  }
}

// Create singleton instance
export const secureLogger = new SecureLogger();

// Export utility functions
export { sanitizeData, obfuscateValue };

export default secureLogger;